import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { TonePill } from './parts';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import type { ShellJob } from '@/lib/shell-job.service';
import { okCount, problemCount, runActivity, runDuration } from '@/lib/shell-job-status';
import { formatInstant, offsetLabel, startedAgo } from '@/lib/run-timeline';
import { SHELL_MONO, TONES, toneText } from '@/theme/tokens';

/** Columns a phone does without: Status, Command and Devices stay. */
const hideSm = { '@media (max-width: 700px)': { display: 'none' } } as const;
const muted = { ...hideSm, color: 'text.secondary', whiteSpace: 'nowrap' } as const;

interface RunsTableProps {
  jobs: ShellJob[];
  loading: boolean;
  /** Set for super admins: adds the Tenant column. */
  tenantNames?: Map<string, string>;
  timezone: string;
  now: number;
}

/** Newest first; a row opens its run. */
export function RunsTable({ jobs, loading, tenantNames, timezone, now }: RunsTableProps) {
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/bulk-shell/${id}`);
  return (
    <TableContainer>
      <Table sx={{ '& th, & td': { px: 1.5 }, '& td': { py: 1.25 } }}>
        <TableHead sx={{ '& .MuiTableCell-head': { textTransform: 'none', py: 1, whiteSpace: 'nowrap' } }}>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Command</TableCell>
            <TableCell sx={hideSm}>Description</TableCell>
            {tenantNames && <TableCell sx={hideSm}>Tenant</TableCell>}
            <TableCell>Devices</TableCell>
            <TableCell sx={hideSm}>Started</TableCell>
            <TableCell sx={hideSm}>Duration</TableCell>
            <TableCell sx={hideSm}>By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton rows={5} columns={tenantNames ? 8 : 7} />
          ) : (
            jobs.map((job) => {
              const activity = runActivity(job);
              const startedIso = job.started_at ?? job.created_at;
              const ended = job.finished_at ? Date.parse(job.finished_at) : now;
              const ok = okCount(job.counts);
              const failed = problemCount(job.counts);
              const pct = (n: number) => `${(n / Math.max(1, job.counts.total)) * 100}%`;
              return (
                <TableRow key={job.id} hover onClick={() => open(job.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Box component="span" title={activity.hint}>
                      <TonePill tone={activity.tone} dot>
                        {activity.label}
                      </TonePill>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    {/* The keyboard and new-tab way in. stopPropagation: the row's click must not also navigate this tab. */}
                    <Link
                      component={RouterLink}
                      to={`/bulk-shell/${job.id}`}
                      onClick={(e) => e.stopPropagation()}
                      noWrap
                      color="inherit"
                      underline="hover"
                      sx={{ display: 'block', fontFamily: SHELL_MONO, fontSize: 12 }}
                    >
                      $ {job.command}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ ...hideSm, maxWidth: 280 }}>
                    {/* One line; the full text on hover. */}
                    <Typography noWrap title={job.description || undefined} sx={{ fontSize: 13, color: job.description ? 'text.primary' : 'text.secondary' }}>
                      {job.description || '—'}
                    </Typography>
                  </TableCell>
                  {tenantNames && <TableCell sx={hideSm}>{tenantNames.get(job.tenant_id) ?? job.tenant_id.slice(0, 8)}</TableCell>}
                  <TableCell sx={{ minWidth: 140 }}>
                    {/* Answered OK, then failed; the rest of the track has not finished. */}
                    <Box aria-hidden sx={{ display: 'flex', width: 110, height: 6, mb: 0.5, borderRadius: '3px', overflow: 'hidden', bgcolor: 'divider' }}>
                      {/* Widths in style, not sx: emotion keeps a class for every distinct value. */}
                      <Box sx={{ bgcolor: TONES.success.main }} style={{ width: pct(ok) }} />
                      <Box sx={{ bgcolor: TONES.error.main }} style={{ width: pct(failed) }} />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {(ok + failed).toLocaleString()} / {job.counts.total.toLocaleString()}
                      {failed > 0 && (
                        <>
                          {' · '}
                          <Box component="span" sx={{ color: toneText('error') }}>
                            {failed.toLocaleString()} failed
                          </Box>
                        </>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell sx={muted} title={`${formatInstant(startedIso, timezone, now, false)} (${offsetLabel(startedIso, timezone)})`}>
                    {startedAgo(startedIso, timezone, now)}
                  </TableCell>
                  <TableCell sx={muted}>{runDuration(ended - Date.parse(startedIso))}</TableCell>
                  <TableCell sx={muted}>
                    <Typography noWrap sx={{ fontSize: 'inherit', maxWidth: 180 }}>
                      {job.created_by}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
